import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, SwitchCamera, Ticket, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { LIVE } from "@/constants/testIds";
import { toast } from "sonner";
import useCameraPreview from "./go-live/useCameraPreview";

// Sanitized Go-Live breadcrumbs (booleans/enums/status only; server is dark
// unless GOLIVE_BREADCRUMBS_ENABLED=true). Fire-and-forget, never blocks UX.
const crumb = (step, extra = {}) => {
  try { api.post("/telemetry/golive", { step, ...extra }).catch(() => {}); } catch { /* noop */ }
};
const isShell = () => {
  try { return window.Capacitor?.isNativePlatform?.() === true; } catch { return false; }
};

/**
 * Slice 1 — simplified Go Live. Fast path: preview + title + Start.
 * The old pre-Live tools wall moved into Creator Control Center → Live
 * Tools (editable mid-stream). The ONE pre-Live exception is Paid Live:
 * admission pricing must be fixed before viewers enter, so it stays here
 * as a small collapsed row and is immutable after go-live.
 */
export default function GoLiveModal({ open, onOpenChange, onStarted }) {
  const { videoRef, facing, flipCamera } = useCameraPreview(open, (ok, errName) =>
    crumb("permission", { ok, err_name: errName })
  );

  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [paidOpen, setPaidOpen] = useState(false);
  const [paidEntry, setPaidEntry] = useState(0);

  // F1 — iOS keyboard guard: when the title input focuses, iOS scrolls the
  // fixed layer up ("form pushed to top"). Pin the visual viewport back.
  useEffect(() => {
    if (!open) return;
    const reset = () => window.scrollTo(0, 0);
    window.visualViewport?.addEventListener("resize", reset);
    window.addEventListener("scroll", reset);
    return () => {
      window.visualViewport?.removeEventListener("resize", reset);
      window.removeEventListener("scroll", reset);
    };
  }, [open]);

  useEffect(() => {
    if (open) crumb("shell", { shell: isShell() });
  }, [open]);

  const submit = async () => {
    if (busy) return;
    crumb("tap", { shell: isShell() });
    setBusy(true);
    try {
      crumb("pre_post");
      const { data } = await api.post("/live", {
        title: title.trim(),
        thumbnail_url:
          "https://images.unsplash.com/photo-1631700023489-2ab7f47f1e75?crop=entropy&cs=srgb&fm=jpg&q=85",
        paid_entry_coins: paidEntry || 0,
        filter: "none",
        beauty: false,
        split_enabled: false,
        poll: null,
        goal_coins: 0,
        wheel: ["Free Gift", "Shoutout", "Follow Back", "Kiss Cam"],
        custom_gifts: [],
        extensions: [],
        // Platform-wide content policy prohibits explicit content in every
        // Live mode, so all streams are iOS-appropriate by definition.
        ios_safe: true,
      });
      toast.success("You're live!");
      crumb("post_status", { code: 200 });
      setTitle("");
      onStarted && onStarted(data);
      onOpenChange(false);
    } catch (e) {
      crumb("post_status", { code: e?.response?.status ?? 0, err_name: e?.name });
      toast.error(e?.response?.data?.detail || "Could not start stream.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black overflow-hidden" data-testid="golive-root">
      <video
        ref={videoRef}
        data-testid={LIVE.goLivePreview}
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-zinc-900 via-black to-rose-950" />

      {/* Top bar — back + title. F1: safe-area-top so the title input never
          sits under the iPhone status bar / Dynamic Island. */}
      <div
        className="absolute top-0 inset-x-0 px-4 pb-4 flex items-start gap-3 z-10"
        style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}
      >
        <button
          data-testid={LIVE.goLiveClose}
          onClick={() => onOpenChange(false)}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white flex items-center justify-center shrink-0"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md border border-white/10 rounded-full px-4 py-3">
            <input
              data-testid={LIVE.goLiveTitle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Add a title (optional)"
              className="flex-1 bg-transparent text-white placeholder-white/60 focus:outline-none text-sm"
            />
          </div>
        </div>
        <button
          type="button"
          data-testid="golive-flip"
          onClick={flipCamera}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white flex items-center justify-center shrink-0"
          title="Flip camera"
        >
          <SwitchCamera className="w-5 h-5" />
        </button>
      </div>

      {/* Bottom — collapsed Paid Live row + big Start Live */}
      <div
        className="absolute bottom-0 inset-x-0 px-4 pt-4 z-10"
        style={{ paddingBottom: "max(1.5rem, calc(env(safe-area-inset-bottom) + 1rem))" }}
      >
        <div className="mb-3">
          <button
            type="button"
            data-testid="golive-paid-toggle"
            onClick={() => setPaidOpen((s) => !s)}
            className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-black/40 backdrop-blur-md border border-white/10 text-white"
            aria-expanded={paidOpen}
          >
            <span className="flex items-center gap-2 text-sm">
              <Ticket size={15} className={paidEntry > 0 ? "text-yellow-300" : "text-white/60"} />
              {paidEntry > 0 ? `Paid Live — ${paidEntry} coins to enter` : "Paid Live (optional)"}
            </span>
            {paidOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          {paidOpen && (
            <div className="mt-2 px-4 py-3 rounded-xl bg-black/50 backdrop-blur-md border border-white/10">
              <p className="text-white/60 text-xs mb-2">
                Entry cost in coins (0 = free). Locked once you go live.
              </p>
              <input
                type="number"
                min="0"
                data-testid="golive-paid-input"
                value={paidEntry || ""}
                onChange={(e) => setPaidEntry(Math.max(0, parseInt(e.target.value || "0", 10)))}
                placeholder="0"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-400/50"
              />
            </div>
          )}
        </div>

        <p className="text-[11px] text-zinc-400 mt-3 mb-3 px-1" data-testid="golive-policy-reminder">
          Reminder: nudity, pornography and sexually explicit content are prohibited in all Live streams — including private, Vault and Community rooms.
        </p>
        <button
          data-testid={LIVE.goLiveSubmit}
          onClick={submit}
          disabled={busy}
          className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold py-4 rounded-full text-base flex items-center justify-center gap-2 transition"
        >
          {busy ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Starting…
            </>
          ) : (
            "Start Live"
          )}
        </button>
      </div>
    </div>,
    document.body
  );
}
